import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { offerBody, offerPageBody, onboardingUi } from '../i18n/onboardingUi.js';
import { applyDocumentLocale, normalizeUiLocale } from '../i18n/locales.js';
import { t } from '../i18n/translate.js';
import { GuidedTour } from './GuidedTour.jsx';
import { resolvePageTourSteps, matchPageTour } from './pages/index.js';
import {
  getOnboardingStatus,
  isOnboardingSnoozedForSession,
  setOnboardingStatus,
  snoozeOnboardingForSession,
} from './storage.js';

/** @type {import('react').Context<null | {
 *   locale: string,
 *   startTour: () => void,
 *   tourActive: boolean,
 * }>} */
const OnboardingContext = createContext(null);

/**
 * @param {string | null | undefined} selector
 * @param {number} timeoutMs
 */
function waitForElement(selector, timeoutMs = 6000) {
  if (!selector) {
    return Promise.resolve(null);
  }
  const existing = document.querySelector(selector);
  if (existing) {
    return Promise.resolve(existing);
  }
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function OfferModal({ locale, roleLabel, pageLabel, onStart, onDismiss, onDisable, disabling }) {
  return createPortal(
    <div className="onboarding-offer-backdrop" role="dialog" aria-modal="true">
      <div className="onboarding-offer-card">
        <h2>{t(onboardingUi.offerTitle, locale)}</h2>
        <p>{pageLabel ? offerPageBody(pageLabel, roleLabel, locale) : offerBody(roleLabel, locale)}</p>
        <p className="muted small">{t(onboardingUi.offerHint, locale)}</p>
        <div className="onboarding-offer-actions">
          <button type="button" className="btn-primary" onClick={onStart}>
            {t(onboardingUi.offerStart, locale)}
          </button>
          <button type="button" className="btn-secondary" onClick={onDismiss} disabled={disabling}>
            {t(onboardingUi.offerLater, locale)}
          </button>
          <button type="button" className="btn-link onboarding-offer-disable" onClick={onDisable} disabled={disabling}>
            {t(onboardingUi.offerDisable, locale)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function NoTourToast({ locale, onClose }) {
  return createPortal(
    <div className="onboarding-toast" role="status">
      <p>{t(onboardingUi.noPageTour, locale)}</p>
      <button type="button" className="btn-link" onClick={onClose}>
        {t(onboardingUi.toastClose, locale)}
      </button>
    </div>,
    document.body,
  );
}

export function OnboardingProvider({ children }) {
  const { user, booting, refreshUser } = useAuth();
  const location = useLocation();
  const locale = normalizeUiLocale(user?.ui_locale);
  const [showOffer, setShowOffer] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [noTour, setNoTour] = useState(false);
  const [offerPageLabel, setOfferPageLabel] = useState('');
  const [disablingOffer, setDisablingOffer] = useState(false);
  const tourSourceRef = useRef('help');

  useEffect(() => {
    applyDocumentLocale(user?.ui_locale);
  }, [user?.ui_locale]);

  const roleLabel = user?.role?.replace(/_/g, ' ') || 'portal';

  const persistOfferStatus = useCallback(
    (status) => {
      if (user?.id && user?.role) {
        setOnboardingStatus(user.id, user.role, status);
      }
    },
    [user],
  );

  const finishTour = useCallback(
    (status, { persist = true } = {}) => {
      if (persist && (status === 'completed' || status === 'dismissed')) {
        persistOfferStatus(status);
      }
      setTourActive(false);
      setStepIndex(0);
      setShowOffer(false);
      tourSourceRef.current = 'help';
    },
    [persistOfferStatus],
  );

  const goToStep = useCallback(async (index, tourSteps) => {
    const step = tourSteps[index];
    if (!step) {
      return;
    }
    if (step.selector) {
      await waitForElement(step.selector);
    }
    setStepIndex(index);
  }, []);

  const runTour = useCallback(
    async (source) => {
      if (!user) {
        return false;
      }
      const tourSteps = await resolvePageTourSteps(user, location.pathname);
      if (!tourSteps.length) {
        return false;
      }
      tourSourceRef.current = source;
      setSteps(tourSteps);
      setShowOffer(false);
      setTourActive(true);
      await goToStep(0, tourSteps);
      return true;
    },
    [user, location.pathname, goToStep],
  );

  const startTour = useCallback(async () => {
    const started = await runTour('help');
    if (!started) {
      setNoTour(true);
      window.setTimeout(() => setNoTour(false), 4500);
    }
  }, [runTour]);

  const handleNext = useCallback(async () => {
    const next = stepIndex + 1;
    if (next >= steps.length) {
      finishTour('completed');
      return;
    }
    await goToStep(next, steps);
  }, [stepIndex, steps, goToStep, finishTour]);

  const handlePrev = useCallback(async () => {
    const prev = Math.max(0, stepIndex - 1);
    await goToStep(prev, steps);
  }, [stepIndex, steps, goToStep]);

  const dismissOffer = useCallback(() => {
    if (user?.id && user?.role) {
      snoozeOnboardingForSession(user.id, user.role);
    }
    setShowOffer(false);
  }, [user]);

  const disableOfferPrompts = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    setDisablingOffer(true);
    try {
      await apiJson('/api/profile', {
        method: 'PUT',
        body: { onboarding_enabled: false },
      });
      await refreshUser();
    } catch {
      // profile field may be unavailable before migration — still disable locally
    } finally {
      persistOfferStatus('dismissed');
      setShowOffer(false);
      setDisablingOffer(false);
    }
  }, [user, refreshUser, persistOfferStatus]);

  const promptsEnabled = user?.onboarding_enabled !== false;

  useEffect(() => {
    if (booting || !user?.id || !user?.role) {
      setShowOffer(false);
      return;
    }
    if (user.impersonation?.active) {
      setShowOffer(false);
      return;
    }
    if (!promptsEnabled) {
      setShowOffer(false);
      return;
    }
    const status = getOnboardingStatus(user.id, user.role);
    if (status === 'completed' || status === 'dismissed') {
      return;
    }
    if (isOnboardingSnoozedForSession(user.id, user.role)) {
      return;
    }
    const pageDef = matchPageTour(location.pathname);
    if (!pageDef || tourActive) {
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const tourSteps = await resolvePageTourSteps(user, location.pathname);
      if (cancelled || !tourSteps.length || tourActive) {
        return;
      }
      setOfferPageLabel(document.querySelector('[data-tour="page-title"]')?.textContent?.trim() || '');
      setShowOffer(true);
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [booting, user, location.pathname, tourActive, promptsEnabled]);

  const value = useMemo(
    () => ({
      locale,
      startTour,
      tourActive,
    }),
    [locale, startTour, tourActive],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {showOffer ? (
        <OfferModal
          locale={locale}
          roleLabel={roleLabel}
          pageLabel={offerPageLabel}
          onStart={() => void runTour('welcome')}
          onDismiss={dismissOffer}
          onDisable={() => void disableOfferPrompts()}
          disabling={disablingOffer}
        />
      ) : null}
      {noTour ? <NoTourToast locale={locale} onClose={() => setNoTour(false)} /> : null}
      <GuidedTour
        active={tourActive}
        steps={steps}
        stepIndex={stepIndex}
        locale={locale}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={() => finishTour('dismissed')}
        onFinish={() => finishTour('completed')}
      />
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return ctx;
}

export function OnboardingHelpButton() {
  const { startTour, locale } = useOnboarding();
  return (
    <button
      type="button"
      className="icon-btn onboarding-help-btn"
      title={t(onboardingUi.tourButtonTitle, locale)}
      aria-label={t(onboardingUi.tourButtonTitle, locale)}
      onClick={() => void startTour()}
    >
      ?
    </button>
  );
}
